/**
 * Login Page for NWGSV Command System
 * Premium black/gold branded authentication
 */
import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      const next = new URLSearchParams(location.search).get('next') || '/';
      const safeNext = next.startsWith('/') && !next.startsWith('//') ? next : '/';
      navigate(safeNext);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="global-watermark">
        <img src="/brand/nwg-logo-web.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
      </div>

      <div style={styles.card}>
        <h1 style={styles.title}>NextWealth™ Global</h1>
        <p style={styles.subtitle}>NWG COMMAND CENTER</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              placeholder="Enter your email"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'AUTHENTICATING...' : 'LOGIN'}
          </button>
        </form>

        <p style={styles.registerLink}>
          <Link to="/forgot-password" style={styles.link} data-testid="forgot-password-link">
            Forgot password?
          </Link>
        </p>

        <p style={styles.registerLink}>
          Don't have an account?{' '}
          <Link to="/register" style={styles.link}>
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'black',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    padding: '40px',
    background: 'rgba(0, 0, 0, 0.95)',
    border: '1px solid #FFD700',
    borderRadius: '8px',
    boxShadow: '0 0 30px rgba(255, 215, 0, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#FFD700',
    textAlign: 'center',
    marginBottom: '8px',
    letterSpacing: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#FFD700',
    textAlign: 'center',
    marginBottom: '32px',
    opacity: 0.7,
    letterSpacing: '1px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#FFD700',
    letterSpacing: '1px',
  },
  input: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    borderRadius: '4px',
    color: '#FFD700',
    fontSize: '14px',
    outline: 'none',
  },
  button: {
    padding: '14px',
    background: '#FFD700',
    border: 'none',
    borderRadius: '4px',
    color: 'black',
    fontSize: '14px',
    fontWeight: 'bold',
    letterSpacing: '1px',
    marginTop: '10px',
  },
  error: {
    padding: '12px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid #ff4444',
    borderRadius: '4px',
    color: '#ff4444',
    fontSize: '14px',
  },
  registerLink: {
    textAlign: 'center',
    marginTop: '24px',
    color: 'rgba(255, 215, 0, 0.7)',
    fontSize: '14px',
  },
  link: {
    color: '#FFD700',
    textDecoration: 'none',
    fontWeight: 'bold',
  },
};
